import { toDate } from '@/lib/formatRelativeDate'
import type {
  ContextGap,
  GapStatus,
  GapStatusCounts,
} from '@/types/api'

/**
 * A re-check normally clears within minutes. If it fails the timestamp can
 * linger, so after this long the row stops claiming it's being checked.
 */
export const RECHECK_STALE_AFTER_MS = 15 * 60 * 1000

export interface PartitionedGaps {
  /** Rows the user can still work on: `open` or `in_progress`. */
  active: ContextGap[]
  /** Closed rows, most recently resolved first. */
  resolved: ContextGap[]
}

/**
 * Split one gap-analysis response into the Fill Gaps page's two lists.
 * A local `resolved` override (from a chat reply) pulls a row out of the
 * active list immediately; it joins `resolved` once a refetch confirms it.
 * Ignored gaps appear in neither list.
 */
export function partitionGaps(
  gaps: ContextGap[],
  overrides: Record<string, GapStatus> = {}
): PartitionedGaps {
  const active: ContextGap[] = []
  const resolved: ContextGap[] = []
  for (const gap of gaps) {
    if (gap.status === 'resolved') resolved.push(gap)
    else if (isActiveStatus(gap.status) && overrides[gap.id] !== 'resolved') {
      active.push(gap)
    }
  }
  resolved.sort((a, b) => resolvedAtMs(b) - resolvedAtMs(a))
  return { active, resolved }
}

export function isActiveStatus(status: GapStatus): boolean {
  return status === 'open' || status === 'in_progress'
}

/**
 * Sidebar badge count: everything still workable (open + in progress).
 * Counts `gaps` only when the response carries no statusCounts.
 */
export function activeGapCount(
  counts: GapStatusCounts | null | undefined,
  gaps: ContextGap[] = []
): number {
  if (counts) return (counts.open ?? 0) + (counts.inProgress ?? 0)
  return gaps.filter((g) => isActiveStatus(g.status)).length
}

/**
 * Epoch ms at which the "checking" state stops showing, or null when no
 * re-check was requested (or the timestamp can't be read).
 */
export function recheckExpiresAt(
  gap: Pick<ContextGap, 'recheckRequestedAt'>
): number | null {
  const requested = toDate(gap.recheckRequestedAt)
  return requested ? requested.getTime() + RECHECK_STALE_AFTER_MS : null
}

/** True while a re-check was requested less than 15 minutes ago. */
export function isRecheckPending(
  gap: Pick<ContextGap, 'recheckRequestedAt'>,
  now: number = Date.now()
): boolean {
  const expiresAt = recheckExpiresAt(gap)
  return expiresAt !== null && now < expiresAt
}

/** Missing / unparseable dates sort last. */
function resolvedAtMs(gap: ContextGap): number {
  return toDate(gap.resolvedAt)?.getTime() ?? 0
}
